import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { deleteLeadNote } from '@user/store/leadNotes/api'
import { deleteOpportunityNote } from '@user/store/opportunityNotes/api'
import { EditNoteDrawer } from '../EditNoteDrawer/EditNoteDrawer'
import { ENTITY_TYPES } from '@sales-center/common'
import format from 'date-fns/format'
import { AlertDialogSlide, MoreButton, toast } from '@sales-center/components'
import { CombineNote } from '../typings'
import { selectUserId } from '@user/store/authorization/userSlice'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import Button from '@mui/material/Button'
import { NoteCardDetailItem } from '@user/components/Notes/EntityNotes/NoteCardDetailItem'
import { useGetServiceNoteUrl } from '@user/store/authorization'
import useTheme from '@mui/material/styles/useTheme'
import { useNote } from '@user/components/Notes/EntityNotes/hooks/useNote'
import { KnockStatusChip } from '@user/features/MapView/KnockStatusChip'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  note: CombineNote
  canUpdate?: boolean
  backToNotes?: boolean
  externalLinkId: string
  onUpdate: () => void
  setBackToNotes?: (value: boolean, id: number) => void
}

export function NoteCard(props: Props) {
  const {
    entityId,
    entityType,
    note: {
      createdBy,
      note,
      id,
      createdById,
      pestPac,
      code,
      address,
      noteExternalLinkId,
      rejectedBy,
      rejectedDate,
      knockStatusNote,
    },
    canUpdate,
    backToNotes,
    setBackToNotes,
    externalLinkId,
    onUpdate,
  } = props

  const dispatch = useAppDispatch()
  const userId = useSelector(selectUserId)
  const notesUrl = useGetServiceNoteUrl(noteExternalLinkId ?? undefined)

  const mounted = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const showActionsMenu = useMemo(
    () => createdById === userId && !knockStatusNote,
    [createdById, userId, knockStatusNote],
  )

  // EDIT NOTE DRAWER
  const [advanceOpen, setAdvanceOpen] = useState<boolean>(false)
  const [editNoteDrawerOpen, setEditNoteDrawerOpen] = useState<boolean>(false)
  const [ongoingDeletion, setOngoingDeletion] = useState<boolean>(false)

  const {
    expanded,
    isOverflowing,
    handleToggleExpand,
    formattedNote,
    noteRef,
  } = useNote(note)

  const theme = useTheme()

  const toggleAdvanceOpen = useCallback(() => {
    setAdvanceOpen(!advanceOpen)
  }, [advanceOpen, setAdvanceOpen])

  const openEditNoteDrawer = useCallback(() => {
    if (backToNotes) {
      if (setBackToNotes) setBackToNotes(true, id)
    } else setEditNoteDrawerOpen(true)
  }, [backToNotes, setBackToNotes, id])

  const closeEditNoteDrawer = useCallback(() => {
    if (backToNotes) {
      if (setBackToNotes) setBackToNotes(false, id)
    } else setEditNoteDrawerOpen(false)
  }, [backToNotes, setBackToNotes, id])

  const onUpdateSuccess = useCallback(() => {
    setEditNoteDrawerOpen(false)
    onUpdate()
  }, [onUpdate])

  // DELETE NOTE DIALOG
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)

  const openDeleteNoteDialog = useCallback(() => {
    setDeleteDialogOpen(true)
  }, [])

  const closeDeleteNoteDialog = useCallback(() => {
    setDeleteDialogOpen(false)
  }, [])

  const handleDeleteNote = useCallback(
    async (id: number) => {
      setOngoingDeletion(true)
      if (entityType == ENTITY_TYPES.Lead) {
        const result = await dispatch(deleteLeadNote(id))

        if (deleteLeadNote.fulfilled.match(result)) {
          toast.success('Note deleted.')
          if (mounted.current) {
            setDeleteDialogOpen(false)
          }
        } else {
          toast.error('Failed to delete note.')
        }
      }

      if (entityType == ENTITY_TYPES.Opportunity) {
        const result = await dispatch(deleteOpportunityNote(id))

        if (deleteOpportunityNote.fulfilled.match(result)) {
          toast.success('Note deleted.')
          if (mounted.current) {
            setDeleteDialogOpen(false)
          }
        } else {
          toast.error('Failed to delete note.')
        }
      }
      setOngoingDeletion(false)
    },
    [dispatch, entityType, setDeleteDialogOpen, setOngoingDeletion],
  )

  const showMenu = showActionsMenu && canUpdate

  return (
    <Stack>
      <Paper
        variant="elevation"
        sx={{
          marginLeft: theme.spacing(0.1),
          marginRight: theme.spacing(0.1),
        }}
      >
        <Stack paddingY={1} paddingX={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            minHeight={48}
          >
            <Stack direction="row" gap={0.5} width="60%" alignItems="center">
              <img
                src={
                  pestPac
                    ? '/static-assets/icons/pest-pac-icon.svg'
                    : '/static-assets/icons/sales-center-icon.svg'
                }
              />
              <Typography variant="subtitle2" color="#0E283FD8">
                {pestPac
                  ? 'PestPac Location Note'
                  : knockStatusNote
                    ? 'Knock Status Note'
                    : 'Sales Center Note'}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              {showMenu && (
                <MoreButton
                  dataTestId="moreNoteBtn"
                  options={[
                    {
                      label: 'Edit',
                      onClick: openEditNoteDrawer,
                      dataTestId: 'editNoteBtn',
                    },
                    {
                      label: 'Delete',
                      onClick: openDeleteNoteDialog,
                      dataTestId: 'deleteNoteBtn',
                    },
                  ]}
                  style={{
                    padding: theme.spacing(1.2),
                    borderRadius: '100px',
                  }}
                />
              )}
            </Stack>
          </Stack>

          {rejectedBy && (
            <Typography variant="body1" mb={1.6}>
              Office Note for Rejected Opportunity Submitted on{' '}
              {format(rejectedDate, "MMMM dd, yyyy 'at' h:mm aa")} by{' '}
              {rejectedBy}
            </Typography>
          )}

          {knockStatusNote ? (
            <>
              <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="subtitle4" ml={0.3}>
                  Knock Status:
                </Typography>
                <KnockStatusChip
                  status={knockStatusNote.knockStatusSettingName}
                  isDefault={
                    knockStatusNote.knockStatusSettingName === 'Do not knock'
                  }
                />
              </Stack>
              <Stack direction="row" alignItems="flex-start" gap={1} mb={1.5}>
                <Typography variant="subtitle4" ml={0.3}>
                  Reason:
                </Typography>
                <Typography variant="body2">
                  {knockStatusNote.customReason || '-'}
                </Typography>
              </Stack>
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                ref={noteRef}
                mb={0.5}
                whiteSpace="break-spaces"
                sx={{ wordBreak: 'break-all' }}
              >
                {formattedNote}
                {isOverflowing && (
                  <span
                    style={{
                      color: theme.palette.primary.main,
                      cursor: 'pointer',
                    }}
                    onClick={handleToggleExpand}
                  >
                    {!expanded ? '... Show more' : ' Show less'}
                  </span>
                )}
              </Typography>
            </>
          )}

          <Button
            variant="text"
            onClick={toggleAdvanceOpen}
            size="small"
            color="primary"
            sx={{
              justifyContent: 'flex-start',
              paddingLeft: theme.spacing(0.5),
              textTransform: 'none',
              width: 'fit-content',
            }}
            endIcon={
              !advanceOpen ? (
                <KeyboardArrowDown fontSize="small" />
              ) : (
                <KeyboardArrowUp fontSize="small" />
              )
            }
            data-test-id="noteCardAdvanceDetailsBtn"
          >
            Additional details
          </Button>

          {advanceOpen && (
            <>
              <NoteCardDetailItem
                label="Added By"
                value={knockStatusNote?.addedByUserName || createdBy}
              />

              {pestPac && (
                <>
                  <NoteCardDetailItem
                    label="Location Address"
                    value={address || ''}
                  />

                  <NoteCardDetailItem label="Note Code" value={code || ''} />

                  <Button
                    variant="text"
                    href={notesUrl}
                    target="_blank"
                    size="small"
                    sx={{
                      color: theme.palette.primary.main,
                      justifyContent: 'flex-start',
                      paddingLeft: theme.spacing(1.3),
                      display: 'flex',
                      gap: 0.8,
                      textTransform: 'none',
                    }}
                    data-test-id="noteCardViewPestPacNoteBtn"
                  >
                    View notes for this location
                    <img src="/static-assets/icons/arrow-northeast-outlined.svg" />
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      </Paper>
      <EditNoteDrawer
        entityId={entityId}
        entityType={entityType}
        noteId={id}
        onClose={closeEditNoteDrawer}
        onUpdateSuccess={onUpdateSuccess}
        open={editNoteDrawerOpen}
        externalLinkId={externalLinkId}
        backToNotes={backToNotes}
      />
      <AlertDialogSlide
        cancelClick={closeDeleteNoteDialog}
        message="Are you sure you want to delete this note?"
        okButtonText="Delete"
        okClick={() => handleDeleteNote(id)}
        open={deleteDialogOpen}
        title="Delete Note"
        okButtonDisabled={ongoingDeletion}
      />
    </Stack>
  )
}
